import React, { createContext, ReactNode, useCallback, useContext, useRef } from "react";
import { isLoginPage, redirectToLogin } from "../context/auth";

export type FetchFunction = (input: RequestInfo, init?: RequestInit) => Promise<Response>;
export type FetchErrorHookFunction = (responseOrError: Response | Error) => void;

const FetchContext = createContext<FetchFunction>(undefined);
const FetchErrorHookSetterContext = createContext<(errorHook: FetchErrorHookFunction) => void>(undefined);

/** Hook to use the custom fetch function (see FetchProvider) */
export const useFetch = () => useContext(FetchContext);

/** Hook to set the error hook that gets called when the response status is 500 or higher */
export const useFetchErrorHookSetter = () => useContext(FetchErrorHookSetterContext);

type FetchProviderProps = {
    children: ReactNode;
};

const USER_DATA_URL = "/api/user-data";

function isApiRequest(input: RequestInfo): boolean {
    const url = new URL(typeof input === "string" ? input : input.url, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith("/api/");
}

/**
 * Whether a 401 from `/api` means the session has ended, rather than a missing permission.
 *
 * `/api` answers 401 for both, so the session is asked directly: `/api/user-data` needs nothing
 * but a logged-in session.
 */
async function sessionHasEnded(): Promise<boolean> {
    const response = await fetch(USER_DATA_URL, { credentials: "same-origin" });
    return response.status === 401;
}

/**
 * FetchProvider is a provider that wraps the children with a fetch function that sends the browser to the login
 * once the session has ended, and calls the error hook if the response status is 500 or higher.
 *
 * No Authorization header is added: this UI's token is kept by the server, and the session cookie is all the
 * browser sends.
 * */
const FetchProvider = ({ children }: FetchProviderProps) => {
    // A ref, not state: setting the hook must not give `fetchWithErrorHandling` a new identity, or every effect
    // that depends on `useFetch()` runs again - `UserDataProvider` fetched the user data twice on every page load.
    const errorHookRef = useRef<FetchErrorHookFunction>(undefined);

    const setErrorHook = useCallback((errorHook: FetchErrorHookFunction) => {
        errorHookRef.current = errorHook;
    }, []);

    // This is a custom implementation of fetch that calls the error hook if the response status is 500 or higher.
    // This also gets injected into Gauss' RequestActionCreator.
    // If the error hook is set, it will be called when the response status is 500 or higher
    // (used to show backend error dialog).
    const fetchWithErrorHandling: FetchFunction = useCallback(async (input, init) => {
        init ??= {};
        init.credentials ??= "same-origin";

        try {
            const response = await fetch(input, init);
            if (response.status >= 500) {
                errorHookRef.current?.(response);
            }
            if (
                response.status === 401 &&
                !isLoginPage(window.location.pathname) &&
                isApiRequest(input) &&
                (await sessionHasEnded())
            ) {
                redirectToLogin();
            }
            return response;
        } catch (e) {
            errorHookRef.current?.(e);
            throw e;
        }
    }, []);

    return (
        <FetchContext.Provider value={fetchWithErrorHandling}>
            <FetchErrorHookSetterContext.Provider value={setErrorHook}>{children}</FetchErrorHookSetterContext.Provider>
        </FetchContext.Provider>
    );
};

export default FetchProvider;
